/**
 * Short local blog posts. Each post links to the deeper guides/service pages and
 * ends at an enquiry form. Posts in DRAFT_POST_SLUGS are noindexed and left out
 * of the blog list's live view, sitemap and navigation until the firm approves them.
 */
import type { LinkProps } from "@tanstack/react-router";
import goldenBeachImage from "@/assets/golden-beach-hero.jpg";
import conveyancingImage from "@/assets/guide-conveyancing.jpg";
import { SELLER_DISCLOSURE_START_LABEL } from "./seller-disclosure";

export type BlogCategory = "Conveyancing tips" | "Local community" | "Wills" | "Probate" | "Legal curiosities";

export type BlogPost = {
  slug: string;
  title: string;
  metaTitle: string;
  description: string;
  date: string; // ISO date
  category: BlogCategory;
  area: string;
  intro: string;
  sections: { heading: string; paragraphs: string[]; bullets?: string[] }[];
  links: { to: NonNullable<LinkProps["to"]>; label: string; params?: Record<string, string> }[];
  cta: string;
  author?: string;
  editorial?: boolean;
  disclaimer?: string;
  image?: { src: string; alt: string };
  sources?: { label: string; url: string }[];
};

export const BLOG_POSTS: BlogPost[] = [
{
  "slug": "melbourne-cup-bets-office-sweeps-queensland",
  "title": "Your horse won. Your mate won’t pay. Now what?",
  "metaTitle": "Melbourne Cup Bets & Office Sweeps: Queensland Law",
  "description": "Is the office sweep legal? Can you enforce a bet against a mate? Michael Klein looks at Queensland betting law and three memorable court cases.",
  "date": "2026-11-03",
  "category": "Legal curiosities",
  "area": "Queensland",
  "author": "Michael Klein",
  "editorial": true,
  "intro": "Have you ever wondered whether the office sweep is legal? Or whether you can enforce a bet against your mate who’s welched one time too many? Probably not. It’s just the sort of thing lawyers think about while everyone else is picking a horse.",
  "sections": [
    {
      "heading": "A bet’s a bet. Or is it?",
      "paragraphs": [
        "It’s Melbourne Cup Day. Someone in the office has the sweep organised, someone else has studied the form, and someone has picked a horse because they like its name.",
        "Then comes the familiar offer: “Twenty dollars says mine beats yours.”",
        "Fair enough. But what happens if your horse wins and your mate won’t pay? Is a bet actually a contract you can enforce?",
        "You might expect a simple yes or no. Unfortunately, you’ve asked a lawyer.\n"
      ]
    },
    {
      "heading": "There’s a catch with the bet between mates",
      "paragraphs": [
        "In Queensland, being allowed to do something and being able to make someone pay for it are two different questions.",
        "Section 255 of the Racing Integrity Act 2016 starts by making contracts relating to betting void, subject to some important exceptions. It also catches certain related promises to pay and stops court claims for money or property won or lost on a bet, held by a stakeholder for the event, or lent or advanced for betting. [1]",
        "In other words, “but you promised” may not get you very far. Neither will writing the bet on a napkin. The problem isn’t necessarily proving what was said; it’s whether the law will enforce it at all.",
        "That provision doesn’t itself make placing the bet a criminal offence. Equally, it isn’t a permission slip for every kind of gambling. The arrangement still has to be looked at under the relevant rules.",
        "And before anyone decides this means bookmakers never have to pay, there are exceptions. We’ll come back to those."
      ]
    },
    {
      "heading": "This argument has been going on for a while",
      "paragraphs": [
        "Back in 1949, Tom Hill took his dispute with William Hill all the way to the House of Lords. He had lost money betting, and the bookmaker tried to recover it through a later repayment agreement, in return for holding off further action through the racing authorities.",
        "Hill won. The English law at the time barred court actions to recover money won on a wager. A new agreement didn’t get the bookmaker around that bar, even though it had promised something in return. The case is Hill v William Hill (Park Lane) Ltd [1949] AC 530. [2]",
        "Not quite the way most of us would choose to spend an afternoon at the races.",
        "That’s a historical English decision, not a shortcut out of paying every betting debt today. The law has since changed in England, Wales and Scotland: section 335 of the Gambling Act 2005, in force since September 2007, says a contract’s connection with gambling doesn’t itself stop it being enforced. Other reasons for refusing enforcement can still apply. [3]",
        "Then there’s the rather unlikely connection between betting and a Victorian flu remedy.",
        "The Carbolic Smoke Ball Company offered £100 to anyone who caught influenza after using its product as directed. Mrs Carlill did just that and asked for her money. The company argued, among other things, that the arrangement was a wager.",
        "The judges weren’t persuaded. In Carlill v Carbolic Smoke Ball Co [1892] 2 QB 484, affirmed [1893] 1 QB 256, the reward promise was upheld and the wagering argument rejected. [4]",
        "Hawkins J described a wager as involving each side’s chance of winning or losing, with neither having another interest in the bargain beyond the stake. That’s a historical description, not the complete test under every modern gambling law. But the point here is straightforward: a promise doesn’t become a bet just because payment depends on something uncertain.",
        "Mrs Carlill got her £100. Calling the promise a wager hadn’t made it disappear."
      ]
    },
    {
      "heading": "And if you lose rather more than twenty dollars?",
      "paragraphs": [
        "In Kakavas v Crown Melbourne Ltd [2013] HCA 25, the amount at stake was $20.5 million in net baccarat losses. The gambler argued that Crown had engaged in unconscionable dealing. [5]",
        "The High Court unanimously dismissed his appeal. The necessary exploitation of a relevant special disadvantage hadn’t been established.",
        "This isn’t a case to joke about: gambling problems can cause real harm. Nor does the decision mean a person with a gambling problem can never obtain legal relief. It means the size of a loss, on its own, doesn’t establish a right to get the money back. This was a Victorian casino case about unconscionable dealing, not the Queensland rule about a private wager."
      ]
    },
    {
      "heading": "So where does that leave today’s bets and the office sweep?",
      "paragraphs": [
        "For Queensland racing bookmakers, section 139 provides that a bet made at a licensed venue as part of lawful bookmaking is a valid contract on which either side can sue. Section 256 also removes the section 255 barrier for betting conducted under specified legislation, including the Wagering Act 1998 and the Charitable and Non-Profit Gaming Act 1999. [1]",
        "The Wagering Act separately makes specified betting under its framework lawful. So a properly regulated wager is not the same thing as your mate’s promise over lunch. Betting rules, mistakes, evidence and ordinary contract issues can still matter. [7]",
        "There’s also an exception for specified contributions towards prizes for lawful games and sports. It’s not a general exemption for any betting pool. [1]",
        "As for an individual’s Queensland office sweep, the category 1 rules allow gross proceeds of up to $2,000, provided the organiser takes no personal gain and all proceeds—or goods of equivalent value—go back to the players as prizes. You don’t have to give anything to charity to use that individual-sweep exception. [6]"
      ]
    },
    {
      "heading": "What about the office sweep?",
      "paragraphs": [
        "You do still have to comply with the applicable gaming rules and prize restrictions. If a prize includes alcohol or a gaming product, entries or ticket payments must not be accepted from minors. That’s not a blanket adults-only rule for every cash-prize sweep, although other restrictions may apply. [6]",
        "My practical suggestion is to sort out the prize split and what happens to scratched horses before anyone hands over money. Keep a record of who paid and which horse they drew.",
        "Pooling money to place bets needs a separate look. Section 11A of the Wagering Act restricts asking to act as someone else’s totalisator-betting agent, and acting as their agent for agreed consideration. Writing down contributions and sharing arrangements helps with evidence; it doesn’t make an otherwise unlawful or void arrangement valid. [7]"
      ]
    },
    {
      "heading": "Pooling bets and betting online",
      "paragraphs": [
        "If you’re betting online, check who you’re actually contracting with, where they’re licensed, the account terms and the complaints process. Your Queensland address doesn’t settle every question about which law applies, and a governing-law clause isn’t necessarily the last word either.",
        "For any disputed bookmaker bet, keep the confirmation, odds, account history and terms, and start with the operator’s complaints process. The appropriate regulator or dispute process may be the next step.",
        "Otherwise, enjoy the afternoon and don’t bet more than you can afford to lose. Just remember that winning the argument about whose horse is better and having an enforceable claim are not quite the same thing."
      ]
    },
    {
      "heading": "Enjoy the race",
      "paragraphs": [
        "Happy Cup Day from Coastside Law."
      ]
    }
  ],
  "image": {
    "src": "https://coastsidelaw.com.au/melbourne-cup-racing-2026.jpg",
    "alt": "Illustrative generated racing image of three horses and jockeys on a green racecourse; not a photograph of the Melbourne Cup"
  },
  "links": [
    {
      "to": "/our-people",
      "label": "Meet Michael and the Coastside Law team"
    },
    {
      "to": "/blog",
      "label": "More from Coastside Law"
    }
  ],
  "cta": "Contact Coastside Law",
  "disclaimer": "General information, not legal advice or a complete guide to running a sweep. The arrangement, jurisdiction and applicable law matter. For gambling support, contact Gambling Help Online on 1800 858 858.",
  "sources": [
    {
      "label": "[1] Racing Integrity Act 2016 (Qld), ss 139, 255–256",
      "url": "https://www.legislation.qld.gov.au/view/pdf/inforce/2026-11-03/act-2016-012"
    },
    {
      "label": "[2] Hill v William Hill (Park Lane) Ltd [1949] AC 530",
      "url": "https://vlex.co.uk/vid/hill-v-william-hill-792961189"
    },
    {
      "label": "[3] Gambling Act 2005 (UK), s 335",
      "url": "https://www.legislation.gov.uk/ukpga/2005/19/section/335"
    },
    {
      "label": "[4] Carlill v Carbolic Smoke Ball Co [1892] 2 QB 484 (trial judgment)",
      "url": "https://www.ato.gov.au/law/view/print?DocID=JUD%2F%2A1892%2A2QBD484%2F00001&PiT=99991231235958"
    },
    {
      "label": "[4] Carlill v Carbolic Smoke Ball Co [1893] 1 QB 256 (appeal)",
      "url": "https://law.justia.com/cases/foreign/united-kingdom/1-q-b-256-1893.html"
    },
    {
      "label": "[5] Kakavas v Crown Melbourne Ltd [2013] HCA 25 — official High Court summary",
      "url": "https://www.hcourt.gov.au/assets/publications/judgment-summaries/2013/hca25-2013-06-05.pdf"
    },
    {
      "label": "[6] Charitable and Non-Profit Gaming Act 1999 (Qld)",
      "url": "https://www.legislation.qld.gov.au/view/pdf/inforce/2026-11-03/act-1999-026"
    },
    {
      "label": "[6] Charitable and Non-Profit Gaming Regulation 1999 (Qld), reg 2B",
      "url": "https://www.legislation.qld.gov.au/view/pdf/inforce/2026-11-03/sl-1999-0283"
    },
    {
      "label": "[7] Wagering Act 1998 (Qld), ss 11, 11A, 12",
      "url": "https://www.legislation.qld.gov.au/view/pdf/inforce/2026-11-03/act-1998-015"
    },
    {
      "label": "[2] Star City Pty Ltd v Tan Hong Woon [2001] SGHC 100 — later discussion of Hill",
      "url": "https://www.elitigation.sg/gd/s/2001_SGHC_100"
    }
  ]
},
  {
    slug: "buying-together-golden-beach-pelican-waters",
    title: "Buying together in Golden Beach or Pelican Waters? Have this conversation first",
    metaTitle: "Buying Together in Golden Beach & Pelican Waters",
    description: "Buying a home together in Golden Beach or Pelican Waters? Discuss unequal deposits, ownership, mortgage responsibilities and an exit plan before signing.",
    date: "2026-10-22",
    category: "Conveyancing tips",
    area: "Golden Beach and Pelican Waters",
    image: { src: conveyancingImage, alt: "Coastal homes and a tree-lined street — illustrative property image" },
    intro: "You have found a home you both like. Before making an offer, pause for a less exciting but important conversation: whose money is going in, whose names will be on the title, and what happens if your plans change? These questions matter whether you are partners, relatives or friends buying together.",
    sections: [
      {
        heading: "An unequal deposit needs more than a handshake",
        paragraphs: [
          "One buyer might bring savings from a previous home while the other contributes more to repayments. Or a parent may help with the deposit. None of that should be left to assumptions about what is fair later. Write down the contributions and tell your solicitor and lender how the purchase is being funded.",
          "If family money is involved, clarify whether it is a gift, a loan or money intended to buy an ownership share. Those are different arrangements. A written agreement may be appropriate, and the people contributing money may need independent advice. An agreement also needs to work with the lender's requirements; it is not a way to keep an arrangement hidden from the bank.",
        ],
      },
      {
        heading: "The title and the mortgage answer different questions",
        paragraphs: [
          "The title records legal ownership. The loan documents set out obligations to the lender. A private arrangement to split repayments does not, by itself, change what the bank can require under those documents. Ask your lender to explain each borrower's liability rather than assuming you are responsible only for your agreed share.",
          "Joint tenancy and tenancy in common also have different consequences, particularly on death. Broadly, a joint tenant's interest passes to the surviving joint tenant or tenants, while a tenant in common's share forms part of their estate. We explain the comparison in our existing joint-ownership guide. The decision here is which structure fits your contributions and family plans, not which label sounds simpler.",
        ],
      },
      {
        heading: "Agree on the awkward situations while things are going well",
        paragraphs: ["A shared purchase should have a plan for change, not just settlement day. Discuss these questions before committing:"],
        bullets: [
          "Who pays rates, insurance, maintenance and unexpected repairs?",
          "What happens if one buyer cannot keep up with repayments?",
          "If one person wants to sell and the other does not, how will you decide what happens?",
          "How would a buyout be valued and funded?",
          "What should happen if a buyer dies or loses decision-making capacity?",
        ],
      },
      {
        heading: "Do not assume you can cheaply change it later",
        paragraphs: [
          "Adding an owner or changing shares after settlement can involve lender consent, transfer documents, tax considerations and transfer duty. Whether duty or a concession applies depends on the transaction and eligibility. Get advice before treating a later transfer as an easy fix.",
          "Buying a home is also a good time to review your will and enduring power of attorney. Your estate plans need to reflect how the property is actually held. A will should not be drafted on the assumption that every jointly owned asset becomes part of the estate.",
        ],
      },
      {
        heading: "What to bring to a pre-signing discussion",
        paragraphs: [
          "Bring the proposed contract, the buyers' full names, a summary of the deposit and repayment contributions, and details of any family funding. Mention an existing will, children from earlier relationships or a planned buyout arrangement if relevant. Each person should have the opportunity to get independent advice where their interests differ.",
          "Coastside Law can discuss the conveyancing and estate-planning questions together, so the ownership choice is considered before documents are signed. Share personal or financial documents only through a channel agreed with your solicitor, not through a public comment or social-media thread.",
        ],
      },
    ],
    links: [
      { to: "/guides/$slug", params: { slug: "joint-tenants-vs-tenants-in-common-queensland" }, label: "Joint tenants and tenants in common: the Queensland comparison" },
      { to: "/golden-beach", label: "Talk to Coastside Law in Golden Beach" },
      { to: "/wills-and-estates", label: "Wills and estate planning" },
      { to: "/stamp-duty-calculator-qld", label: "Estimate Queensland transfer duty" },
    ],
    sources: [
      { label: "Titles Queensland: survivorship and recording the death of a joint tenant", url: "https://www.titlesqld.com.au/manual-guides/guides-to-common-registry-transactions/dealing-with-a-death/recording-the-death-of-a-joint-tenant/" },
      { label: "Queensland Revenue Office: home and change-of-tenure duty exemptions", url: "https://qro.qld.gov.au/duties/transfer-duty/exemptions/home-property-owner/" },
      { label: "Queensland Revenue Office: transfer duty", url: "https://qro.qld.gov.au/duties/transfer-duty/" },
    ],
    cta: "Discuss your shared purchase before signing",
  },
  {
    slug: "established-home-golden-beach-renovation-checks",
    title: "Buying an established Golden Beach home? Check the renovations, not just the rooms",
    metaTitle: "Golden Beach Home Purchases: Renovation Checks",
    description: "Buying an established Golden Beach home? Check extensions, decks, plumbing records and easements, and understand what seller disclosure does not tell you.",
    date: "2026-10-22",
    category: "Conveyancing tips",
    area: "Golden Beach and Caloundra",
    image: { src: conveyancingImage, alt: "Established coastal homes — illustrative property image" },
    intro: "An enclosed patio, a new bathroom or a converted garage can make an established house feel just right. The useful question is not only whether the work looks good. It is whether the records, approvals and intended use match what you are buying. Here is how to investigate without confusing a building inspection with a legal check.",
    sections: [
      {
        heading: "Start with what has changed",
        paragraphs: [
          "At the inspection, note additions and alterations: a deck, shed, carport, extra bathroom, enclosed space or pool. Ask the agent what work was done, when it was done and what records the seller holds. An answer such as 'it was like that when we bought it' is a reason to investigate, not proof of approval.",
          "Do not assume that every alteration needed the same approval, or that every older structure is unlawful. Requirements depend on the work and the rules applying at the time. Equally, age alone does not prove a structure is approved. A solicitor can identify the records to request; a building certifier or council may need to clarify the technical position.",
        ],
      },
      {
        heading: "A building inspection and a records search do different jobs",
        paragraphs: [
          "A building and pest inspection assesses matters within the inspector's scope, including condition and accessible defects. It is not automatically a council approvals audit. Tell the inspector about alterations that concern you and ask what their report will and will not cover.",
          "Sunshine Coast Council offers property searches covering building, plumbing and development information, as well as other property records. Search availability, fees and turnaround differ. Ask which searches suit this property and what gaps may remain, rather than ordering a generic package and assuming it answers everything.",
        ],
      },
      {
        heading: "Check the land beneath the improvements",
        paragraphs: [
          "A title search and registered plan can identify the lot and registered interests such as easements. Compare that information with how you expect to use the yard. A planned extension, pool or shed may need more investigation if an easement or other restriction affects its proposed location.",
          "The title does not tell you whether a bathroom was approved or a deck is structurally sound. Planning information, approvals records and physical inspections answer different questions. Your due diligence should connect those pieces rather than treating one document as a complete picture.",
        ],
      },
      {
        heading: "Form 2 is the starting point, not a condition report",
        paragraphs: [
          `Queensland's seller disclosure scheme began on ${SELLER_DISCLOSURE_START_LABEL}. It generally requires sellers to provide the disclosure statement and prescribed documents before the buyer signs, subject to exceptions. That does not mean every renovation, defect or property risk is covered.`,
          "The Queensland Government specifically identifies matters outside the scheme, including building and pest condition, structural soundness, flooding history and building or development approvals. Read the disclosure carefully, then investigate the questions it does not answer. Termination rights for disclosure problems have conditions; do not assume any missing detail lets you walk away.",
        ],
      },
      {
        heading: "Resolve important gaps before you commit",
        paragraphs: [
          "If the extra room is essential to your purchase, say so before signing. Your solicitor can advise on further enquiries and whether a tailored condition is appropriate. A standard building and pest condition should not be assumed to give a broad right to terminate for any approvals concern. An extension of time also depends on the contract and the other party's agreement.",
          "Send the contract and disclosure documents with a short list of the improvements you want checked. Include your plans for the property. That helps focus the review on the house you are actually buying, not just its address and purchase price.",
        ],
      },
    ],
    links: [
      { to: "/guides/$slug", params: { slug: "unapproved-building-work-decks-sheds-pools-queensland" }, label: "Unapproved building work: decks, sheds and pools" },
      { to: "/guides/$slug", params: { slug: "due-diligence-clause-queensland" }, label: "How a Queensland due-diligence condition works" },
      { to: "/form-2-buyer-rights-qld", label: "Form 2 and Queensland buyer rights" },
      { to: "/conveyancing/$suburb", params: { suburb: "caloundra" }, label: "Caloundra conveyancing and contract review" },
    ],
    sources: [
      { label: "Sunshine Coast Council: property searches", url: "https://www.sunshinecoast.qld.gov.au/development/searches/property-searches" },
      { label: "Queensland Government: seller disclosure scheme and its exclusions", url: "https://www.qld.gov.au/housing/buying-owning-home/seller-disclosure-scheme" },
    ],
    cta: "Ask about an established-home purchase",
  },
  {
    slug: "golden-beach-pumicestone-passage-coastal-change",
    title: "Golden Beach and the changing Pumicestone Passage: where residents can find reliable information",
    metaTitle: "Golden Beach Coastal Change: A Local Resident Guide",
    description: "Golden Beach coastal change explained: Bribie Island breakthrough updates, council storm-tide information and how residents can check an individual property.",
    date: "2026-10-22",
    category: "Local community",
    area: "Golden Beach and Pumicestone Passage",
    image: { src: goldenBeachImage, alt: "Golden Beach waterfront overlooking Pumicestone Passage — site cover image, not hazard mapping" },
    intro: "The Passage and foreshore are part of daily life at Golden Beach. They are also changing coastal environments. If you live here, or are considering a move, it helps to separate council information from speculation about what a change means for a particular home. This is a starting point for finding reliable updates, not a risk assessment of the suburb.",
    sections: [
      {
        heading: "Why the Bribie Island breakthrough matters locally",
        paragraphs: [
          "Sunshine Coast Council's Bribie Island breakthrough action plan focuses on protecting the Golden Beach and Diamond Head foreshores. Council's storm-tide mapping update explains that further study followed the 2022 breakthrough. Those are useful sources for understanding why the foreshore and coastal hazards are being discussed together.",
          "Responsibilities are shared. Council's action-plan page explains that the State manages Bribie Island and that Pumicestone Passage sits within Moreton Bay Marine Park. A question about a foreshore project is not necessarily directed to the same authority as a question about island management or marine-park activities.",
        ],
      },
      {
        heading: "Erosion, storm tide and flooding are not interchangeable",
        paragraphs: [
          "Coastal erosion concerns changes to the shoreline. Storm-tide inundation concerns coastal land being covered by elevated sea levels during storm conditions. Flood information can address other processes, such as catchment flooding. Do not assume one map answers every question about water at an address.",
          "Council's coastal hazard adaptation strategy considers erosion, storm-surge inundation and expanding tidal areas associated with sea-level rise. A strategy describes long-term planning; it is not a promise about future works at one home or a forecast of what will happen on a particular day.",
        ],
      },
      {
        heading: "Does Golden Beach flood? Start with the address",
        paragraphs: [
          "There is no responsible single yes-or-no answer for every property in Golden Beach. Check council information for the specific lot, what type of hazard is mapped, and the scenario or assumptions used. Then ask council or a suitably qualified adviser what the information means for your question.",
          "Council's storm-tide update makes an important distinction: more detailed research does not itself create a new physical risk. Updated information can improve understanding of an existing hazard. A map also should not be read as proof that a property has flooded in the past, or that an unmapped property has no exposure.",
        ],
      },
      {
        heading: "A useful checklist for residents and future residents",
        paragraphs: ["Use original sources and keep the question specific:"],
        bullets: [
          "Read the council breakthrough action plan for the project context and the agencies involved.",
          "Check the date and scope of the council storm-tide and property-search information you are using.",
          "For a purchase or renovation, check the individual lot and relevant planning information rather than relying on a suburb-wide claim.",
          "Ask an insurer about cover, exclusions and costs for the actual address; a council map is not an insurance offer.",
          "Use council's disaster information for current warnings and emergency preparation, rather than a property article or an old social-media post.",
        ],
      },
      {
        heading: "Keep community updates separate from purchase advice",
        paragraphs: [
          "A discussion about a public foreshore project is not evidence that a particular home will gain or lose value, become uninsurable or receive a protective structure. This article makes none of those claims. For current project status, works and consultation opportunities, follow the responsible authority's latest notices.",
          "If you are buying, tell your solicitor which coastal or flooding questions matter to you before signing. The review can identify appropriate searches and whether the contract needs a tailored condition. Queensland seller disclosure is not a substitute for investigating flooding history or the risks relevant to your intended use.",
          "For neighbours who simply want to understand local changes, the council resources below are the best next step. A legal enquiry is only needed when you have a property or contract question specific to your situation.",
        ],
      },
    ],
    links: [
      { to: "/golden-beach", label: "Coastside Law's Golden Beach office" },
      { to: "/guides/$slug", params: { slug: "buying-a-house-you-cannot-insure-flood-storm-surge-queensland" }, label: "Flood, storm surge and insurance: questions for property buyers" },
      { to: "/form-2-buyer-rights-qld", label: "What Queensland seller disclosure covers" },
    ],
    sources: [
      { label: "Sunshine Coast Council: Bribie Island breakthrough action plan", url: "https://www.sunshinecoast.qld.gov.au/environment/rivers-and-coast/breakthrough-action-plan" },
      { label: "Sunshine Coast Council: coastal hazard adaptation strategy", url: "https://www.sunshinecoast.qld.gov.au/council/planning-and-projects/council-strategies/coastal-hazard-adaptation-strategy" },
      { label: "Sunshine Coast Council: storm-tide mapping update (2024)", url: "https://www.sunshinecoast.qld.gov.au/news/council-updates-storm-tide-mapping-for-coast" },
      { label: "Sunshine Coast Council: property searches", url: "https://www.sunshinecoast.qld.gov.au/development/searches/property-searches" },
      { label: "Sunshine Coast Council: disaster information and warnings", url: "https://disaster.sunshinecoast.qld.gov.au/" },
      { label: "Queensland Government: seller disclosure scheme", url: "https://www.qld.gov.au/housing/buying-owning-home/seller-disclosure-scheme" },
    ],
    cta: "Ask a Golden Beach property question",
  },
  {
    slug: "buying-in-caloundra-checks-before-you-sign",
    title: "Buying in Caloundra or Golden Beach? Five checks before you sign",
    metaTitle: "Buying in Caloundra: 5 Checks Before You Sign",
    description:
      "Buying a home in Caloundra, Golden Beach or Pelican Waters? Five things to check before you sign a Queensland contract, from a local conveyancing solicitor.",
    date: "2026-10-03",
    category: "Conveyancing tips",
    area: "Caloundra and Golden Beach",
    intro:
      "Most problems in a Queensland purchase are locked in the moment you sign. Once the contract is signed, your options narrow quickly. Here are the five things we check for buyers before they put pen to paper.",
    sections: [
      {
        heading: "1. Read the seller's disclosure (Form 2)",
        paragraphs: [
          "Since 1 August 2025 most Queensland sellers must give you a disclosure statement before you sign. It covers the title, easements, council and body corporate details and some notices. Read it before the contract, not after.",
        ],
      },
      {
        heading: "2. Check the finance and building and pest dates",
        paragraphs: [
          "These dates decide how long you have to get your loan approved and your inspections done. If they're too tight, ask for more time before you sign. It's much harder to get an extension later.",
        ],
      },
      {
        heading: "3. Look at the special conditions",
        paragraphs: [
          "Special conditions change the standard contract. Some are harmless. Others shift risk onto you, such as a short settlement or a clause that lets the seller keep fittings you assumed were included.",
        ],
      },
      {
        heading: "4. Know what cooling-off actually gives you",
        paragraphs: [
          "Most residential buyers get five business days to cool off, but walking away costs 0.25% of the price. Cooling-off is a safety net, not a substitute for checking the contract first.",
        ],
      },
      {
        heading: "5. Units and townhouses: read the body corporate details",
        paragraphs: [
          "Check the levies, any special levies coming up, and whether there are disputes or major repairs on the horizon. Coastal buildings can need costly work on concrete, balconies and waterproofing.",
        ],
      },
      {
        heading: "Send us the contract first",
        paragraphs: [
          "When we act on your purchase, we review the contract before you sign at no extra cost. A purchase is a fixed $1,499 including GST.",
        ],
      },
    ],
    links: [
      { to: "/form-2-buyer-rights-qld", label: "Form 2 seller disclosure explained" },
      { to: "/cooling-off-period-qld", label: "The QLD cooling-off period" },
      { to: "/special-conditions-contract-qld", label: "Special conditions in QLD contracts" },
      { to: "/conveyancing/$suburb", params: { suburb: "caloundra" }, label: "Conveyancing in Caloundra" },
    ],
    cta: "Send us your contract to review",
  },
  {
    slug: "selling-on-the-redcliffe-peninsula-get-ready-before-listing",
    title: "Selling on the Redcliffe Peninsula? Get these ready before you list",
    metaTitle: "Selling in Redcliffe: Get Ready Before You List",
    description:
      "Selling in Redcliffe, Scarborough or Newport? What Queensland sellers need ready before listing, including the Form 2 disclosure statement. Fixed $990 sale fee.",
    date: "2026-10-03",
    category: "Conveyancing tips",
    area: "Redcliffe, Scarborough and Newport",
    intro:
      "If you're selling on the Redcliffe Peninsula, the legal work starts before the sign goes up. Queensland sellers now have to give buyers a disclosure statement before a contract is signed, so it pays to have it ready.",
    sections: [
      {
        heading: "The disclosure statement must come first",
        paragraphs: [
          "Most sellers must give buyers a Form 2 disclosure statement and the required documents before the buyer signs. If it's missing or wrong, the buyer may be able to end the contract. Having it prepared early means you don't lose a keen buyer while it's being sorted.",
        ],
      },
      {
        heading: "What we'll ask you for",
        paragraphs: ["Gathering these early speeds everything up:"],
        bullets: [
          "Your latest rates and water notices",
          "Body corporate details, if it's a unit or townhouse",
          "Any pool safety certificate, or confirmation there's no pool",
          "Details of any approvals for extensions, sheds or granny flats",
          "Your mortgage lender's details so we can arrange discharge",
        ],
      },
      {
        heading: "Mortgage discharge takes time",
        paragraphs: [
          "Banks can take a couple of weeks to process a discharge request. Lodging it as soon as you have a contract helps you settle on time.",
        ],
      },
      {
        heading: "A fixed fee from our Scarborough office",
        paragraphs: [
          "A sale is a fixed $990 including GST, agreed before we start. Our Scarborough office is at 15 Fortune Street.",
        ],
      },
    ],
    links: [
      { to: "/seller-disclosure", label: "Seller disclosure in Queensland" },
      { to: "/redcliffe-conveyancing", label: "Redcliffe conveyancing" },
      { to: "/conveyancing-cost-qld", label: "What conveyancing costs in QLD" },
    ],
    cta: "Get your seller disclosure started",
  },
  {
    slug: "what-happens-on-settlement-day-qld",
    title: "What actually happens on settlement day in Queensland",
    metaTitle: "What Happens on Settlement Day in QLD",
    description:
      "Settlement day in Queensland explained simply: how electronic settlement works, when you get the keys, and what buyers and sellers should do that week.",
    date: "2026-10-03",
    category: "Conveyancing tips",
    area: "Queensland",
    intro:
      "Settlement day is when the money moves and ownership changes. In Queensland it almost always happens online, and you don't need to be anywhere for it. Here's what goes on behind the scenes.",
    sections: [
      {
        heading: "It happens online",
        paragraphs: [
          "Your lawyer, the other side's lawyer and the banks all sign off in an electronic settlement workspace, usually PEXA. When everyone is ready, the funds move and the transfer is lodged for registration at the same time.",
        ],
      },
      {
        heading: "When you get the keys",
        paragraphs: [
          "Once we confirm settlement has gone through, we let you and the agent know. The agent then releases the keys. That's often early to mid afternoon, but it depends on the banks.",
        ],
      },
      {
        heading: "The week before: what to do",
        bullets: [
          "Buyers: book your pre-settlement inspection and confirm your funds are ready",
          "Sellers: move out, leave inclusions in place and hand keys to the agent",
          "Both: answer your lawyer and bank quickly — delays usually start with a missing signature",
        ],
        paragraphs: [],
      },
    ],
    links: [
      { to: "/pexa-settlement", label: "How PEXA settlement works" },
      { to: "/guides/$slug", params: { slug: "pre-settlement-inspection-checklist-queensland" }, label: "Pre-settlement inspection checklist" },
      { to: "/conveyancing-sunshine-coast", label: "Conveyancing on the Sunshine Coast" },
    ],
    cta: "Ask about your settlement",
  },
  {
    slug: "signs-your-will-needs-updating-qld",
    title: "Five signs your will needs updating",
    metaTitle: "Five Signs Your Will Needs Updating (QLD)",
    description:
      "Married, separated, had children or bought property since you made your will? Five signs your Queensland will needs updating. Solicitor wills from $660.",
    date: "2026-10-03",
    category: "Wills",
    area: "Golden Beach and Scarborough",
    intro:
      "A will is written for your life as it was on the day you signed it. When life changes, the will can quietly stop doing what you want. These are the five changes we see most often.",
    sections: [
      {
        heading: "1. You've married",
        paragraphs: [
          "In Queensland, marriage generally cancels an earlier will unless it was made with that marriage in mind. Many people don't realise this.",
        ],
      },
      {
        heading: "2. You've separated or divorced",
        paragraphs: [
          "Divorce usually cancels gifts to a former spouse, but separation alone doesn't. If you've separated and haven't divorced, your old will may still leave everything to your ex.",
        ],
      },
      {
        heading: "3. You've had children or grandchildren",
        paragraphs: [
          "You may want to name a guardian, or set up a trust so young beneficiaries don't receive money outright at 18.",
        ],
      },
      {
        heading: "4. You've bought property or a business",
        paragraphs: [
          "How you own property matters. Jointly owned property usually passes to the other owner, whatever your will says.",
        ],
      },
      {
        heading: "5. Your executor is no longer the right person",
        paragraphs: [
          "If your executor has moved away, fallen ill or you've lost touch, pick someone who can actually do the job.",
        ],
      },
      {
        heading: "A fixed price",
        paragraphs: [
          "A simple will is from $660 including GST, and an enduring power of attorney is from $440. We can see you at Golden Beach or Scarborough.",
        ],
      },
    ],
    links: [
      { to: "/fixed-price-will-solicitor", label: "Fixed-price solicitor will" },
      { to: "/testamentary-trusts", label: "Testamentary trusts" },
      { to: "/wills-and-estates", label: "Wills and estates" },
    ],
    cta: "Book a will review",
  },
  {
    slug: "first-steps-as-an-executor-queensland",
    title: "Named as executor? Your first steps in Queensland",
    metaTitle: "Named as Executor? First Steps in Queensland",
    description:
      "Just been named executor of a Queensland estate? The first steps to take, what not to do yet, and when you'll need probate. From a local estates solicitor.",
    date: "2026-10-03",
    category: "Probate",
    area: "Sunshine Coast and Moreton Bay",
    intro:
      "Being named executor usually comes at a hard time. The good news is that very little has to happen in the first few weeks. Here's where to start, and what to leave until later.",
    sections: [
      {
        heading: "Find the original will",
        paragraphs: [
          "The court needs the original, not a copy. Check the person's papers, and ask the law firm that made the will whether they hold it in safe custody.",
        ],
      },
      {
        heading: "Secure the assets",
        paragraphs: [
          "Make sure the house is locked and insured, and tell the banks about the death so accounts are frozen. Keep receipts for anything you pay.",
        ],
      },
      {
        heading: "Don't hand anything out yet",
        paragraphs: [
          "Family provision claims in Queensland can be started up to nine months after death. Paying beneficiaries too early can leave you personally exposed if a claim comes in.",
        ],
      },
      {
        heading: "Find out whether you need probate",
        paragraphs: [
          "Banks and the Titles Office often need probate before they'll release funds or transfer property. Before applying, notice of the application must be published, and the court then needs time to process it.",
        ],
      },
    ],
    links: [
      { to: "/probate-checklist-queensland", label: "Executor probate checklist" },
      { to: "/probate-queensland", label: "Probate in Queensland" },
      { to: "/probate-enquiry", label: "Probate enquiry" },
    ],
    cta: "Talk to us about the estate",
  },
  {
    slug: "conveyancing-costs-sunshine-coast-2026",
    title: "What conveyancing really costs on the Sunshine Coast in 2026",
    metaTitle: "Conveyancing Costs Sunshine Coast 2026 | Itemised",
    description:
      "An itemised look at Sunshine Coast conveyancing costs in 2026: the professional fee, searches, government fees and transfer duty, from a local solicitor.",
    date: "2026-10-12",
    category: "Conveyancing tips",
    area: "Sunshine Coast",
    intro:
      "When people compare conveyancing quotes on the Sunshine Coast, they usually compare the headline fee. That's only part of the cost. Here is what a purchase or sale actually involves, and where quotes tend to grow.",
    sections: [
      {
        heading: "The professional fee",
        paragraphs: [
          "This is what the firm charges for its work. Ours is fixed: $990 to sell and $1,499 to buy, both including GST. Check whether a quote says \"from\", and whether calls, emails, file set-up or PEXA handling are charged on top.",
        ],
      },
      {
        heading: "Searches and government fees",
        paragraphs: [
          "Buyers pay for title, council and other searches, plus registration fees. On a typical Sunshine Coast purchase these come to about $450 to $800. A unit adds a body corporate search, and acreage or canal property may need more enquiries.",
        ],
      },
      {
        heading: "Transfer duty",
        paragraphs: [
          "For buyers, transfer duty is usually the biggest cost by far. It depends on the price and whether a home or first home concession applies. It is paid to the Queensland Revenue Office, not to your conveyancer.",
        ],
      },
      {
        heading: "Where quotes grow",
        paragraphs: ["Watch for these when comparing quotes:"],
        bullets: [
          "Hourly charges once the matter becomes \"complex\"",
          "Separate fees for off-the-plan or body corporate work",
          "Fees for settlement problems, such as a delayed settlement or extension",
          "Being charged in full when the contract falls over during cooling-off",
        ],
      },
    ],
    links: [
      { to: "/conveyancing-sunshine-coast", label: "Sunshine Coast conveyancing" },
      { to: "/stamp-duty-calculator-qld", label: "Queensland stamp duty calculator" },
      { to: "/fees", label: "Our full fee schedule" },
    ],
    cta: "Want a fixed, itemised quote for your Sunshine Coast property? Send us the address and we'll reply the same business day.",
  },
  {
    slug: "buying-a-unit-kings-beach-body-corporate",
    title: "Buying a unit at Kings Beach: reading the body corporate records",
    metaTitle: "Buying a Unit Kings Beach | Body Corporate Checks",
    description:
      "Buying a unit at Kings Beach or elsewhere in Caloundra? What to look for in the body corporate records before cooling-off ends, from a local solicitor.",
    date: "2026-10-12",
    category: "Conveyancing tips",
    area: "Caloundra",
    intro:
      "When you buy a unit at Kings Beach, you're also buying into a building and the committee that runs it. The records tell you how well that building has been looked after and what it is likely to cost you. Here's what we look for.",
    sections: [
      {
        heading: "The sinking fund",
        paragraphs: [
          "The sinking fund pays for major works. Compare its balance with the forecast. An older beachfront building with a small fund is a warning sign that a special levy may be coming.",
        ],
      },
      {
        heading: "Minutes of recent meetings",
        paragraphs: [
          "Committee and general meeting minutes show what is being discussed: concrete cancer, balcony repairs, lift replacement, waterproofing or legal disputes. This is often where you'll find a problem that isn't mentioned anywhere else.",
        ],
      },
      {
        heading: "Insurance",
        paragraphs: [
          "Building insurance for coastal schemes has risen sharply. Check the premium, the excess and any claims history, because they feed directly into your levies.",
        ],
      },
      {
        heading: "By-laws and letting",
        paragraphs: [
          "If you plan to holiday-let or have a pet, the by-laws and any letting agreement decide whether you can. Read them before you rely on rental income.",
        ],
      },
    ],
    links: [
      { to: "/conveyancing/$suburb", label: "Caloundra conveyancing", params: { suburb: "caloundra" } },
      { to: "/guides/$slug", label: "The Queensland cooling-off period", params: { slug: "cooling-off-period-queensland" } },
    ],
    cta: "Buying a unit in Caloundra? Send us the contract and we'll check it and the body corporate records before cooling-off ends.",
  },
  {
    slug: "preparing-a-probate-application-queensland",
    title: "Getting a probate application right the first time",
    metaTitle: "Preparing a Probate Application in Queensland",
    description:
      "What to gather before applying for probate in Queensland, the hold-ups that delay a grant, and what happens after it issues. Michael Klein explains.",
    date: "2026-11-04",
    category: "Probate",
    area: "Sunshine Coast and Moreton Bay",
    author: "Michael Klein",
    intro:
      "Most executors I meet expect probate to be the hard part. Usually it isn't. A standard Queensland application is dealt with on the papers, and nobody goes to court. What slows things down is nearly always the same few problems, and most of them can be sorted out before anything is filed. Here's what I'd want you to know before you start.",
    sections: [
      {
        heading: "First, check you actually need it",
        paragraphs: [
          "Not every estate needs a grant. A house owned as joint tenants passes to the surviving owner automatically. Superannuation and life insurance often get paid outside the will. Some banks will release a small balance without probate.",
          "Where the person owned real estate in their own name, or held larger amounts with banks or share registries, you'll usually need one. Ask each institution what it requires before you spend money on an application.",
        ],
      },
      {
        heading: "What to gather before you call",
        paragraphs: [
          "If you bring these to the first meeting, we can usually get the notice out straight away:",
        ],
        bullets: [
          "The original signed will and any codicils, not a copy. Don't unstaple it, write on it or remove anything attached",
          "The death certificate",
          "A rough list of assets and debts: property, bank accounts, shares, vehicles, super and any loans",
          "Contact details for the other executors and the beneficiaries",
          "Anything that explains a name difference, such as a maiden name or a name the person was known by",
        ],
      },
      {
        heading: "The notice and the 14-day wait",
        paragraphs: [
          "Before filing, the executor has to publish a notice of intention to apply for a grant and serve a copy on the Public Trustee of Queensland. The application can't be filed until at least 14 days after that.",
          "That fortnight isn't wasted. It's when we finalise the executor's affidavit, so the application is ready to file as soon as the waiting period ends.",
        ],
      },
      {
        heading: "The hold-ups I see most often",
        paragraphs: [
          "If the registry isn't satisfied with something, it sends a requisition, which is a written list of things to fix before the grant will issue. The usual causes are:",
        ],
        bullets: [
          "The person's name is different on the will and the death certificate",
          "Handwritten changes or marks on the will that nobody has explained",
          "A staple mark or page that suggests something was attached and later removed",
          "An affidavit that was signed or witnessed incorrectly",
          "A will signed close to death where the death certificate mentions a condition like dementia",
        ],
      },
      {
        heading: "Dealing with them up front",
        paragraphs: [
          "Every one of those can usually be explained in a short extra affidavit filed with the application. It's far quicker to deal with them at the start than to wait for a requisition and then go back to the court.",
        ],
      },
      {
        heading: "After the grant",
        paragraphs: [
          "The grant gives you authority to deal with the estate. It doesn't finish the job. You still collect the assets, pay the debts, sort out tax and keep accounts before anything goes to the beneficiaries.",
          "Be careful with timing. In Queensland, someone who wants to make a family provision claim generally has to give notice within six months of the death and start proceedings within nine months. Distributing too early can leave you personally exposed, so get advice before paying anyone out.",
        ],
      },
    ],
    links: [
      { to: "/probate", label: "Probate on the Sunshine Coast" },
      { to: "/guides/$slug", label: "How to apply for probate in Queensland: step by step", params: { slug: "how-to-apply-for-probate-in-queensland" } },
      { to: "/guides/$slug", label: "Probate notices in Queensland", params: { slug: "probate-notices-queensland" } },
      { to: "/probate-queensland", label: "Probate in Queensland explained" },
    ],
    cta: "Been named executor? Tell us a little about the estate and we'll explain the next step and our fixed fee.",
    disclaimer: "This is general information about Queensland law, not legal advice about your situation.",
    sources: [
      { label: "Uniform Civil Procedure Rules 1999 (Qld), Chapter 15 (probate and administration)", url: "https://www.legislation.qld.gov.au/view/html/inforce/current/sl-1999-0111" },
      { label: "Succession Act 1981 (Qld), including Part 4 (family provision)", url: "https://www.legislation.qld.gov.au/view/html/inforce/current/act-1981-069" },
      { label: "Queensland Law Reporter (published notices)", url: "https://www.qlr.com.au/" },
    ],
  },
];

/** Posts awaiting the firm's approval. Remove a slug to publish that post. */
export const DRAFT_POST_SLUGS: string[] = [
  "conveyancing-costs-sunshine-coast-2026",
  "buying-a-unit-kings-beach-body-corporate",
];

export const isDraftPost = (slug: string) => DRAFT_POST_SLUGS.includes(slug);
export const getPost = (slug: string) => BLOG_POSTS.find((p) => p.slug === slug);
export const publishedPosts = () => BLOG_POSTS.filter((p) => !isDraftPost(p.slug));
